import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Save, Clock4, CheckCircle, XCircle, PlayCircle,
} from 'lucide-react';

import { usePermission }   from '@/hooks/usePermission';
import { Permissions }     from '@/constants/permissions';
import { AppRoutes }       from '@/constants/routes';
import { Spinner }         from '@/components/ui/Spinner';
import { ConfirmDialog }   from '@/components/ui/ConfirmDialog';
import { cn }              from '@/utils/cn';
import { apiClient }       from '@/api/client';
import { AccountLookupField } from '@/components/erp/AccountLookupField';
import { currencyApi }     from '@/api/currency.api';
import type { CurrencyListDto } from '@/types/currency.types';

// ── Types ─────────────────────────────────────────────────────────────────────
interface AccrualScheduleLineDto {
  id: string;
  lineNumber: number;
  periodCode: string;
  recognitionDate: string;
  amount: number;
  status: string;
  generatedJournalId?: string;
  reversalJournalId?: string;
  postedDate?: string;
}

interface AccrualScheduleDto {
  id: string;
  scheduleCode: string;
  description: string;
  accrualType: string;
  primaryAccountId: string;
  primaryAccountCode: string;
  offsetAccountId: string;
  offsetAccountCode: string;
  totalAmount: number;
  currencyCode: string;
  exchangeRate: number;
  startDate: string;
  endDate?: string;
  totalPeriods: number;
  periodsRecognized: number;
  recognitionFrequency: string;
  isAutoReversal: boolean;
  status: string;
  amountPerPeriod: number;
  remainingAmount: number;
  lines: AccrualScheduleLineDto[];
  createdDate: string;
}

interface CreateAccrualForm {
  scheduleCode: string;
  description: string;
  accrualType: string;
  primaryAccountCode: string;
  primaryAccountId: string;
  primaryAccountName: string;
  offsetAccountCode: string;
  offsetAccountId: string;
  offsetAccountName: string;
  totalAmount: string;
  currencyCode: string;
  exchangeRate: string;
  startDate: string;
  totalPeriods: string;
  recognitionFrequency: string;
  isAutoReversal: boolean;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const ACCRUAL_TYPES = ['Accrual', 'Prepayment'];
const FREQUENCIES   = ['Monthly', 'Quarterly', 'Yearly', 'OneTime'];

const STATUS_COLORS: Record<string, string> = {
  Active:    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  Completed: 'bg-teal-100  text-teal-700  dark:bg-teal-900/30  dark:text-teal-400',
  Cancelled: 'bg-red-100   text-red-700   dark:bg-red-900/30   dark:text-red-400',
  Draft:     'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
};

const LINE_STATUS_COLORS: Record<string, string> = {
  Pending:  'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  Posted:   'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  Reversed: 'bg-slate-100 text-slate-500 dark:bg-slate-700   dark:text-slate-400',
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const defaultForm = (): CreateAccrualForm => ({
  scheduleCode: '',
  description: '',
  accrualType: 'Accrual',
  primaryAccountCode: '',
  primaryAccountId: '',
  primaryAccountName: '',
  offsetAccountCode: '',
  offsetAccountId: '',
  offsetAccountName: '',
  totalAmount: '',
  currencyCode: 'USD',
  exchangeRate: '1',
  startDate: new Date().toISOString().split('T')[0],
  totalPeriods: '12',
  recognitionFrequency: 'Monthly',
  isAutoReversal: false,
});

const inputCls = cn(
  'w-full text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2',
  'bg-white dark:bg-slate-700 text-slate-900 dark:text-white',
  'focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-shadow',
);

const LabeledField = ({
  label, required, error, children,
}: { label: string; required?: boolean; error?: string; children: React.ReactNode }) => (
  <div>
    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
  </div>
);

// These are handed whatever the API sends, including nothing. A summary card that cannot render one
// figure should show a dash for that figure — not take the whole screen down with it, which is what
// `undefined.toLocaleString()` was doing the moment a schedule arrived without an amount on it.
const fmtDate = (d?: string | null) => {
  if (!d) return '—';
  const parsed = new Date(d);
  return Number.isNaN(parsed.getTime())
    ? '—'
    : parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtAmt = (n?: number | null, ccy = '') =>
  n == null ? '—' : `${ccy ? ccy + ' ' : ''}${n.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
const fmtRate = (n?: number | null) => (n == null ? '—' : n.toFixed(4));

// ── Page ──────────────────────────────────────────────────────────────────────
export const AccrualFormPage = () => {
  const { id }            = useParams<{ id: string }>();
  const navigate          = useNavigate();
  const { hasPermission } = usePermission();
  const isCreate          = !id;

  const canCreate  = hasPermission(Permissions.ACCRUAL_CREATE);
  const canExecute = hasPermission(Permissions.ACCRUAL_EXECUTE);
  const canDelete  = hasPermission(Permissions.ACCRUAL_DELETE);

  const [form,        setForm]        = useState<CreateAccrualForm>(defaultForm());
  const [record,      setRecord]      = useState<AccrualScheduleDto | null>(null);
  const [loading,     setLoading]     = useState(!isCreate);
  const [saving,      setSaving]      = useState(false);
  const [errors,      setErrors]      = useState<Record<string, string>>({});
  const [apiError,    setApiError]    = useState<string | null>(null);
  const [recognizing, setRecognizing] = useState<string | null>(null);
  const [currencies,  setCurrencies]  = useState<CurrencyListDto[]>([]);
  const [recognizeConfirmLineId, setRecognizeConfirmLineId] = useState<string | null>(null);
  const [cancelConfirmOpen,      setCancelConfirmOpen]      = useState(false);
  const [cancelling,             setCancelling]             = useState(false);

  const setF = (key: keyof CreateAccrualForm, val: string | boolean) =>
    setForm(f => ({ ...f, [key]: val }));

  // Active currencies only — a schedule must not be created in a retired currency (QA #1).
  useEffect(() => {
    currencyApi.getList({ pageSize: 100, isActive: true })
      .then(res => setCurrencies(res.data.data?.items ?? []))
      .catch(() => { /* leaves the current value as the only option */ });
  }, []);

  // ── Load existing record ───────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setApiError(null);
    try {
      const res = await apiClient.get(`/accruals/${id}`);
      setRecord(res.data?.data ?? null);
    } catch {
      setApiError('Failed to load accrual schedule.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { if (id) load(); }, [id, load]);

  // ── Account selection (searchable lookup, QA account-by-heart) ──────────────
  const setAccount = (field: 'primary' | 'offset', accId: string | null, code: string | null, name: string | null) => {
    setForm(f => field === 'primary'
      ? { ...f, primaryAccountId: accId ?? '', primaryAccountCode: code ?? '', primaryAccountName: name ?? '' }
      : { ...f, offsetAccountId:  accId ?? '', offsetAccountCode:  code ?? '', offsetAccountName:  name ?? '' });
    setErrors(e => ({ ...e, [`${field}Account`]: '' }));
  };

  // ── Validate ───────────────────────────────────────────────────────────────
  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!form.scheduleCode.trim())       e.scheduleCode   = 'Code is required';
    if (!form.description.trim())        e.description    = 'Description is required';
    if (!form.primaryAccountCode.trim()) e.primaryAccount = 'Primary account is required';
    if (!form.primaryAccountId)          e.primaryAccount = 'Invalid account — tab out of the field to validate';
    if (!form.offsetAccountCode.trim())  e.offsetAccount  = 'Offset account is required';
    if (!form.offsetAccountId)           e.offsetAccount  = 'Invalid account — tab out of the field to validate';
    const amt = parseFloat(form.totalAmount);
    if (isNaN(amt) || amt <= 0)          e.totalAmount    = 'Total amount must be greater than 0';
    const periods = parseInt(form.totalPeriods);
    if (isNaN(periods) || periods < 1)   e.totalPeriods   = 'Periods must be 1 or more';
    if (!form.startDate)                 e.startDate      = 'Start date is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setApiError(null);
    try {
      await apiClient.post('/accruals', {
        scheduleCode:        form.scheduleCode.trim().toUpperCase(),
        description:         form.description.trim(),
        accrualType:         form.accrualType,
        primaryAccountId:    form.primaryAccountId,
        primaryAccountCode:  form.primaryAccountCode,
        offsetAccountId:     form.offsetAccountId,
        offsetAccountCode:   form.offsetAccountCode,
        totalAmount:         parseFloat(form.totalAmount),
        currencyCode:        form.currencyCode,
        exchangeRate:        parseFloat(form.exchangeRate) || 1,
        startDate:           form.startDate,
        totalPeriods:        parseInt(form.totalPeriods),
        recognitionFrequency: form.recognitionFrequency,
        isAutoReversal:      form.isAutoReversal,
      });
      navigate(AppRoutes.ACCRUALS_LIST);
    } catch (err: any) {
      setApiError(
        err.response?.data?.errors?.[0]?.message
        ?? err.response?.data?.message
        ?? err.response?.data?.title
        ?? 'Save failed. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  // ── Recognize ──────────────────────────────────────────────────────────────
  const handleRecognize = (lineId: string) => {
    if (!record) return;
    setRecognizeConfirmLineId(lineId);
  };

  const executeRecognize = async () => {
    const lineId = recognizeConfirmLineId;
    if (!record || !lineId) return;
    setRecognizing(lineId);
    setApiError(null);
    try {
      await apiClient.post(`/accruals/${record.id}/recognize`, { lineId });
      setRecognizeConfirmLineId(null);
      await load();
    } catch (err: any) {
      setApiError(err.response?.data?.message ?? 'Recognition failed.');
    } finally {
      setRecognizing(null);
    }
  };

  // ── Cancel ─────────────────────────────────────────────────────────────────
  const handleCancel = () => {
    if (!record) return;
    setCancelConfirmOpen(true);
  };

  const executeCancel = async () => {
    if (!record) return;
    setCancelling(true);
    setApiError(null);
    try {
      await apiClient.post(`/accruals/${record.id}/cancel`, { reason: 'Cancelled by user' });
      setCancelConfirmOpen(false);
      await load();
    } catch (err: any) {
      setApiError(err.response?.data?.message ?? 'Cancel failed.');
    } finally {
      setCancelling(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  if (loading) {
    return <div className="flex h-full items-center justify-center"><Spinner size="lg" /></div>;
  }

  // Asked for a schedule that is not there — a stale link, or one that has since been reset away.
  // Say so. Falling through to a blank create form silently pretends the URL meant something else.
  if (!isCreate && !record) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
        <XCircle size={28} className="text-slate-400" />
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {apiError ?? 'This accrual schedule no longer exists.'}
        </p>
        <button
          onClick={() => navigate(AppRoutes.ACCRUALS_LIST)}
          className="px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-white dark:hover:bg-slate-800"
        >
          Back to Accruals
        </button>
      </div>
    );
  }

  // ── Detail / Read-only view ───────────────────────────────────────────────
  if (!isCreate && record) {
    return (
      <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
        {/* Header */}
        <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(AppRoutes.ACCRUALS_LIST)}
                className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors"
              >
                <ArrowLeft size={18} />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                  <Clock4 size={20} className="text-teal-600 dark:text-teal-400" />
                  {record.scheduleCode}
                </h1>
                <p className="text-sm text-slate-500 dark:text-slate-400">{record.description}</p>
              </div>
              <span className={cn('px-2.5 py-0.5 rounded-full text-xs font-medium', STATUS_COLORS[record.status] ?? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400')}>
                {record.status}
              </span>
            </div>
            {canDelete && ['Draft', 'Active'].includes(record.status) && (
              <button
                onClick={handleCancel}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <XCircle size={15} /> Cancel Schedule
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-auto p-6 space-y-5">
          {apiError && (
            <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-400">
              {apiError}
            </div>
          )}

          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Total Amount',   value: fmtAmt(record.totalAmount, record.currencyCode) },
              { label: 'Per Period',     value: fmtAmt(record.amountPerPeriod, record.currencyCode) },
              { label: 'Remaining',      value: fmtAmt(record.remainingAmount, record.currencyCode) },
              { label: 'Progress',       value: `${record.periodsRecognized} / ${record.totalPeriods} periods` },
            ].map(c => (
              <div key={c.label} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
                <p className="text-xs text-slate-500 dark:text-slate-400">{c.label}</p>
                <p className="mt-1 font-semibold text-slate-900 dark:text-white font-mono text-sm">{c.value}</p>
              </div>
            ))}
          </div>

          {/* Details */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">Schedule Details</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-y-3 gap-x-6 text-sm">
              {[
                { label: 'Type',           value: record.accrualType },
                { label: 'Frequency',      value: record.recognitionFrequency },
                { label: 'Currency',       value: record.currencyCode },
                { label: 'Exchange Rate',  value: fmtRate(record.exchangeRate) },
                { label: 'Start Date',     value: fmtDate(record.startDate) },
                { label: 'End Date',       value: record.endDate ? fmtDate(record.endDate) : '—' },
                { label: 'Auto Reversal',  value: record.isAutoReversal ? 'Yes' : 'No' },
                { label: 'Created',        value: fmtDate(record.createdDate) },
              ].map(f => (
                <div key={f.label}>
                  <span className="text-slate-500 dark:text-slate-400 text-xs">{f.label}</span>
                  <p className="font-medium text-slate-900 dark:text-white mt-0.5">{f.value}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-xs text-slate-500 dark:text-slate-400">Primary Account</span>
                <p className="font-mono font-semibold text-teal-700 dark:text-teal-400 mt-0.5">{record.primaryAccountCode}</p>
              </div>
              <div>
                <span className="text-xs text-slate-500 dark:text-slate-400">Offset Account</span>
                <p className="font-mono font-semibold text-teal-700 dark:text-teal-400 mt-0.5">{record.offsetAccountCode}</p>
              </div>
            </div>
          </div>

          {/* Recognition lines */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">Recognition Schedule</h2>
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 dark:border-slate-700">
                    <th className="py-2 px-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider w-10">#</th>
                    <th className="py-2 px-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Period</th>
                    <th className="py-2 px-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Recognition Date</th>
                    <th className="py-2 px-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Amount</th>
                    <th className="py-2 px-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    {canExecute && record.status === 'Active' && (
                      <th className="py-2 px-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider w-32">Action</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                  {record.lines.map(line => (
                    <tr key={line.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                      <td className="py-2 px-3 text-slate-500 dark:text-gray-400 text-xs">{line.lineNumber}</td>
                      <td className="py-2 px-3 font-mono text-slate-700 dark:text-slate-300 text-xs">{line.periodCode}</td>
                      <td className="py-2 px-3 text-slate-600 dark:text-slate-400 text-xs">{fmtDate(line.recognitionDate)}</td>
                      <td className="py-2 px-3 text-right font-mono text-slate-900 dark:text-white">{fmtAmt(line.amount)}</td>
                      <td className="py-2 px-3 text-center">
                        <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', LINE_STATUS_COLORS[line.status] ?? 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-gray-400')}>
                          {line.status}
                        </span>
                      </td>
                      {canExecute && record.status === 'Active' && (
                        <td className="py-2 px-3 text-right">
                          {line.status === 'Pending' && (
                            <button
                              onClick={() => handleRecognize(line.id)}
                              disabled={!!recognizing}
                              title="Recognize now"
                              className="flex items-center gap-1 ml-auto px-2.5 py-1 text-xs font-medium rounded text-teal-700 dark:text-teal-400 border border-teal-200 dark:border-teal-800 hover:bg-teal-50 disabled:opacity-40 transition-colors"
                            >
                              {recognizing === line.id ? <Spinner size="sm" /> : <PlayCircle size={12} />}
                              Recognize
                            </button>
                          )}
                          {line.status === 'Posted' && (
                            <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1 justify-end">
                              <CheckCircle size={12} /> Posted
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <ConfirmDialog
          isOpen={recognizeConfirmLineId !== null}
          variant="warning"
          title="Recognize Period"
          message="Recognize this period now? A GL journal will be created."
          confirmLabel="Yes, Recognize"
          cancelLabel="No"
          loading={!!recognizing}
          onConfirm={executeRecognize}
          onCancel={() => setRecognizeConfirmLineId(null)}
        />

        <ConfirmDialog
          isOpen={cancelConfirmOpen}
          variant="delete"
          title="Cancel Accrual Schedule"
          message={<span>Cancel accrual schedule <strong>{record.scheduleCode}</strong>? This cannot be undone.</span>}
          confirmLabel="Yes, Cancel"
          cancelLabel="No"
          loading={cancelling}
          onConfirm={executeCancel}
          onCancel={() => setCancelConfirmOpen(false)}
        />
      </div>
    );
  }

  // ── Create form ──────────────────────────────────────────────────────────
  if (!isCreate && !record) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-slate-400">
        <Clock4 size={40} className="mb-3 opacity-40" />
        <p className="text-sm">Accrual schedule not found.</p>
        <button onClick={() => navigate(AppRoutes.ACCRUALS_LIST)} className="mt-3 text-teal-600 dark:text-teal-400 text-sm hover:underline">
          Back to list
        </button>
      </div>
    );
  }

  const amtPerPeriod = parseFloat(form.totalAmount || '0') / (parseInt(form.totalPeriods || '1') || 1);

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(AppRoutes.ACCRUALS_LIST)}
              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors"
            >
              <ArrowLeft size={18} />
            </button>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <Clock4 size={20} className="text-teal-600 dark:text-teal-400" />
              New Accrual Schedule
            </h1>
          </div>
          {canCreate && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg disabled:opacity-60 transition-colors"
            >
              {saving ? <Spinner size="sm" /> : <Save size={15} />}
              {saving ? 'Saving...' : 'Save Schedule'}
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto p-6 space-y-5">
        {apiError && (
          <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-400">
            {apiError}
          </div>
        )}

        {/* Schedule Information */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">Schedule Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <LabeledField label="Schedule Code" required error={errors.scheduleCode}>
              <input
                value={form.scheduleCode}
                onChange={e => setF('scheduleCode', e.target.value.toUpperCase())}
                placeholder="e.g. ACC-001"
                className={cn(inputCls, errors.scheduleCode ? 'border-red-400 focus:ring-red-400' : '')}
              />
            </LabeledField>
            <LabeledField label="Description" required error={errors.description}>
              <input
                value={form.description}
                onChange={e => setF('description', e.target.value)}
                placeholder="e.g. Monthly prepaid rent accrual"
                className={cn(inputCls, errors.description ? 'border-red-400 focus:ring-red-400' : '')}
              />
            </LabeledField>
            <LabeledField label="Accrual Type" required>
              <select value={form.accrualType} onChange={e => setF('accrualType', e.target.value)} className={inputCls}>
                {ACCRUAL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </LabeledField>
          </div>
        </div>

        {/* Accounts */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">Accounts</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
            Search the chart of accounts and pick the account — codes are not typed by hand.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <LabeledField label="Primary Account (Accrual / Prepayment Account)" required error={errors.primaryAccount}>
              <AccountLookupField
                label=""
                value={form.primaryAccountId || null}
                displayCode={form.primaryAccountCode}
                displayName={form.primaryAccountName}
                isPostingAccount
                placeholder="Search account…"
                onChange={(accId, code, name) => setAccount('primary', accId, code, name)}
              />
            </LabeledField>
            <LabeledField label="Offset Account (Income / Expense Account)" required error={errors.offsetAccount}>
              <AccountLookupField
                label=""
                value={form.offsetAccountId || null}
                displayCode={form.offsetAccountCode}
                displayName={form.offsetAccountName}
                isPostingAccount
                placeholder="Search account…"
                onChange={(accId, code, name) => setAccount('offset', accId, code, name)}
              />
            </LabeledField>
          </div>
        </div>

        {/* Amount & Currency */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">Amount & Currency</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <LabeledField label="Total Amount" required error={errors.totalAmount}>
              <input
                type="number"
                value={form.totalAmount}
                onChange={e => setF('totalAmount', e.target.value)}
                placeholder="0.00"
                min={0}
                step={0.01}
                className={cn(inputCls, errors.totalAmount ? 'border-red-400 focus:ring-red-400' : '')}
              />
            </LabeledField>
            <LabeledField label="Currency">
              <select value={form.currencyCode} onChange={e => setF('currencyCode', e.target.value)} className={inputCls}>
                {form.currencyCode && !currencies.some(c => c.code === form.currencyCode) && (
                  <option value={form.currencyCode}>{form.currencyCode}</option>
                )}
                {currencies.map(c => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
              </select>
            </LabeledField>
            <LabeledField label="Exchange Rate">
              <input
                type="number"
                value={form.exchangeRate}
                onChange={e => setF('exchangeRate', e.target.value)}
                min={0}
                step={0.000001}
                className={inputCls}
              />
            </LabeledField>
          </div>
        </div>

        {/* Recognition Schedule */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">Recognition Schedule</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <LabeledField label="Start Date" required error={errors.startDate}>
              <input
                type="date"
                value={form.startDate}
                onChange={e => setF('startDate', e.target.value)}
                className={cn(inputCls, errors.startDate ? 'border-red-400 focus:ring-red-400' : '')}
              />
            </LabeledField>
            <LabeledField label="Total Periods" required error={errors.totalPeriods}>
              <input
                type="number"
                value={form.totalPeriods}
                onChange={e => setF('totalPeriods', e.target.value)}
                min={1}
                step={1}
                className={cn(inputCls, errors.totalPeriods ? 'border-red-400 focus:ring-red-400' : '')}
              />
            </LabeledField>
            <LabeledField label="Recognition Frequency">
              <select value={form.recognitionFrequency} onChange={e => setF('recognitionFrequency', e.target.value)} className={inputCls}>
                {FREQUENCIES.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </LabeledField>
            <LabeledField label="Auto-Reversal">
              <div className="flex items-center gap-2 mt-2.5">
                <input
                  type="checkbox"
                  id="autoReversal"
                  checked={form.isAutoReversal}
                  onChange={e => setF('isAutoReversal', e.target.checked)}
                  className="w-4 h-4 rounded text-teal-600 dark:text-teal-400 border-slate-300 dark:border-gray-600 focus:ring-teal-500"
                />
                <label htmlFor="autoReversal" className="text-sm text-slate-700 dark:text-slate-300">
                  Auto-reverse on next period
                </label>
              </div>
            </LabeledField>
          </div>

          {/* Preview */}
          {form.totalAmount && parseInt(form.totalPeriods) > 0 && (
            <div className="mt-4 p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg border border-teal-100 dark:border-teal-800">
              <p className="text-sm text-teal-800 dark:text-teal-300">
                <strong>{fmtAmt(amtPerPeriod, form.currencyCode)}</strong> will be recognized per period
                over <strong>{form.totalPeriods}</strong> {form.recognitionFrequency.toLowerCase()} period(s)
                starting <strong>{form.startDate ? fmtDate(form.startDate) : '—'}</strong>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
